import { decimal } from '@snap-types/schemas/decimal';
import { z } from 'zod';

import { uuid } from '../common';
import {
  alphaDimensionUnitSchema,
  AlphaQuoteEvaluationStatus,
  alphaSiteSchema,
} from '../enums';
import { base } from './base';
import { strictCalculatedData, strictInputs } from './strict';

export const inputs = {
  ...strictInputs,
  fastenerType: z.string().nullish(),
  material: z.string().nullish(),
  dfars: z.boolean().nullish(),
  threadSize: z.string().nullish(),
  threadPitch: z.string().nullish(),
  length: decimal.nullish(),

  headStyle: z.string().nullish(),
  recess: z.string().nullish(),
  serration: z.boolean().nullish(),

  surfaceFinish: z.string().nullish(),
  postFinish: z.string().nullish(),
};

export const alphaQuoteLineRelaxedInputsSchema = z.object(inputs);

export type AlphaQuoteLineInputs = z.output<
  typeof alphaQuoteLineRelaxedInputsSchema
>;

export const calculatedData = {
  ...strictCalculatedData,

  calculatedDescription: z.string().nullish(),

  dimensionUnit: alphaDimensionUnitSchema.nullish(),
  baseMaterial: z.string().nullish(),
  thread: z.string().nullish(),
  minimumQuantity: decimal.nullish(),
  convertedLength: decimal.nullish(),

  finalHeadVolume: decimal.nullish(),
  shoulderVolume: decimal.nullish(),
  threadVolume: decimal.nullish(),
  preTrimVolume: decimal.nullish(),
  finalVolume: decimal.nullish(),

  preTrimWeight: decimal.nullish(),
  finalWeight: decimal.nullish(),

  rawMaterialSize: decimal.nullish(),
  cutoffLength: decimal.nullish(),
  diametersInHead: decimal.nullish(),
  threadBlankDiameter: decimal.nullish(),
  reductionInArea: decimal.nullish(),

  toolingOperation: z.string().nullish(),

  headingOperation: z.string().nullish(),
  headingRate: decimal.nullish(),
  headingSetupHours: decimal.nullish(),
  headingSetupCost: decimal.nullish(),
  headingSetupLaborCost: decimal.nullish(),
  headingSetupBurdenCost: decimal.nullish(),
  headingSetupLabor: decimal.nullish(),
  headingSetupBurden: decimal.nullish(),
  headingRunLabor: decimal.nullish(),
  headingRunBurden: decimal.nullish(),

  rollingOperation: z.string().nullish(),
  rollingRate: decimal.nullish(),
  rollingSetupHours: decimal.nullish(),
  rollingSetupCost: decimal.nullish(),
  rollingSetupLaborCost: decimal.nullish(),
  rollingSetupBurdenCost: decimal.nullish(),
  rollingSetupLabor: decimal.nullish(),
  rollingSetupBurden: decimal.nullish(),
  rollingRunLabor: decimal.nullish(),
  rollingRunBurden: decimal.nullish(),

  dephosRequired: z.boolean().nullish(),

  manufacturerMaterialId: z.string().nullish(),

  driveStyleAdder: decimal.nullish(),
  threadFormingAdder: decimal.nullish(),
  matThreadAdder: decimal.nullish(),

  materialLeadTimeWeeks: z.number().int().nullish(),
  headingLeadTimeWeeks: z.number().int().nullish(),
  rollingLeadTimeWeeks: z.number().int().nullish(),
  finishingLeadTimeWeeks: z.number().int().nullish(),
  orderEntryLeadTimeWeeks: z.number().int().nullish(),
  toolingLeadTimeWeeks: z.number().int().nullish(),

  manufacturerSiteCode: alphaSiteSchema.nullish(),

  siteUuid: uuid.nullish(),
};

export const alphaQuoteLineCalculatedDataSchema = z.object(calculatedData);

export type AlphaQuoteLineCalculatedData = z.output<
  typeof alphaQuoteLineCalculatedDataSchema
>;

export const relaxed = {
  ...base,
  ...inputs,
  ...calculatedData,
  evaluationStatus: z.enum([
    AlphaQuoteEvaluationStatus.CALCULATION_ERROR,
    AlphaQuoteEvaluationStatus.CONSULT_ENGINEERING,
    AlphaQuoteEvaluationStatus.INCOMPLETE,
  ]),
};

export const alphaQuoteLineRelaxedSchema = z.object(relaxed);

export type AlphaQuoteLine = z.output<typeof alphaQuoteLineRelaxedSchema>;
